import { apiClient } from './client';

// Alineado con aws-serverless/src/services/aging.ts (F3).

export const AGING_BUCKETS = ['0-30', '31-60', '61-90', '90+'] as const;
export type AgingBucket = (typeof AGING_BUCKETS)[number];

export interface AgingBreakdown {
  buckets: Record<AgingBucket, number>;
  current: number;
  totalOutstanding: number;
}

export interface CustomerAging extends AgingBreakdown {
  customerId: string;
  currency: string;
}

/** Cartera de una moneda. Sin conversión: cada moneda va por separado. */
export interface CurrencyAging extends AgingBreakdown {
  currency: string;
  byCustomer: CustomerAging[];
}

/** Con varias monedas los escalares vienen en null: usar byCurrency. */
export interface AgingReport {
  asOf: string;
  currencies: string[];
  byCurrency: Record<string, CurrencyAging>;
  buckets: Record<AgingBucket, number> | null;
  current: number | null;
  totalOutstanding: number | null;
  avgDaysOverdue: number | null;
  byCustomer: CustomerAging[] | null;
}

export const fetchAgingReport = async (asOf?: string, ranchoId?: string | null): Promise<AgingReport> =>
  (await apiClient.get('/api/invoices/aging', {
    params: asOf || ranchoId ? { ...(asOf ? { asOf } : {}), ...(ranchoId ? { ranchoId } : {}) } : undefined,
  })).data;

// ── Clientes, órdenes de venta (M08-4/5) ────────────────────────────────────

export interface Customer {
  customerId: string;
  name: string;
  country?: string;
  creditLimit?: number;
  paymentTermsDays: number;
  currency: string;
  status: string;
}

export const fetchCustomers = async (): Promise<Customer[]> =>
  (await apiClient.get('/api/customers')).data.items;

export interface SaleOrderItem {
  cropName: string;
  grade?: string;
  qty: number;
  unit: string;
  unitPrice: number;
  /** Precio objetivo fijado al crear la orden — se compara contra unitPrice para medir varianza. */
  targetPrice?: number;
}

export interface SaleOrder {
  saleOrderId: string;
  customerId: string;
  items: SaleOrderItem[];
  totalAmount: number;
  orderDate: string;
  status: string;
  currency: string;
}

export const fetchSaleOrdersByRancho = async (ranchoId?: string | null): Promise<SaleOrder[]> =>
  (await apiClient.get('/api/sale-orders', { params: ranchoId ? { ranchoId } : undefined })).data.items;

/** Sin filtro de rancho (usable directo como `queryFn`). */
export const fetchSaleOrders = async (): Promise<SaleOrder[]> => fetchSaleOrdersByRancho();

/** Aviso (no bloqueo) cuando la venta excede lo disponible del lote (B2-6). */
export interface SaleStockWarning {
  loteId: string;
  requestedKg: number;
  availableKg: number;
  harvestedKg: number;
  mermaKg: number;
  soldKg: number;
  overByKg: number;
}

export interface CreateSaleOrderInput {
  loteId?: string;
  customerId: string;
  items: SaleOrderItem[];
  currency: string;
  orderDate: string;
  incoterm?: string;
  projectId?: string;
}

export const createSaleOrder = async (input: CreateSaleOrderInput): Promise<SaleOrder & { stockWarning?: SaleStockWarning }> =>
  (await apiClient.post('/api/sale-orders', input)).data;

export interface CreateCustomerInput {
  name: string;
  country?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  paymentTermsDays?: number;
  creditLimit?: number;
  currency?: string;
  status?: string;
  notes?: string;
}

export const createCustomer = async (input: CreateCustomerInput): Promise<Customer> =>
  (await apiClient.post('/api/customers', input)).data;
